import { HttpClient } from "./http-client";
import { Admin as AdminClass } from "./Admin";
import { Applications as ApplicationsClass } from "./Applications";
import { Approvals as ApprovalsClass } from "./approvals";
import { Assets as AssetsClass } from "./Assets";
import { Calendar as CalendarClass } from "./Calendar";
import { Core as CoreClass } from "./Core";
import { Databases as DatabasesClass } from "./Databases";
import { Health as HealthClass } from "./Health";
import { Inbox as InboxClass } from "./Inbox";
import { Listings as ListingsClass } from "./Listings";
import { Maintenance as MaintenanceClass } from "./Maintenance";
import { Marketplace as MarketplaceClass } from "./Marketplace";
import { Notes as NotesClass } from "./Notes";
import { Organizations as OrganizationsClass } from "./Organizations";
import { Properties as PropertiesClass } from "./Properties";
import { Public as PublicClass } from "./Public";
import { Rentals as RentalsClass } from "./Rentals";
import { BuildingExpenses as BuildingExpensesClass } from "./BuildingExpenses";
import { Contracts as ContractsClass } from "./Contracts";
import { LegalNotices as LegalNoticesClass } from "./LegalNotices";
import { Memberships as MembershipsClass } from "./Memberships";
import { StampTax as StampTaxClass } from "./StampTax";
import { TrustDeposits as TrustDepositsClass } from "./TrustDeposits";
import { NotaryAuthorization as NotaryAuthorizationClass } from "./NotaryAuthorization";
import { Support as SupportClass } from "./Support";
import { Tasks as TasksClass } from "./Tasks";
import { User as UserClass } from "./User";
import { Homysign as HomysignClass } from "./Homysign";
import { Homytrust as HomytrustClass } from "./Homytrust";
import { WorkOrders as WorkOrdersClass } from "./WorkOrders";
import { MaintenanceParts as MaintenancePartsClass } from "./MaintenanceParts";
import { GuestSession as GuestSessionClass } from "./GuestSession";
import { Workflows as WorkflowsClass } from "./Workflows";
import { Payments as PaymentsClass } from "./Payments";
import { Agents as AgentsClass } from "./Agents";
import { Dossiers as DossiersClass } from "./Dossiers";
import { Platform as PlatformClass } from "./Platform";
import * as Models from "./data-contracts";
import { openUpgradeModal } from "@/core/stores/upgradeModalStore";
import { tokenStorage } from "@/core/services/tokenStorage";
import { clearAuthSession, saveAuthSession } from "@/features/core/auth/authStorage";

const apiBaseURL =
  import.meta.env.DEV && import.meta.env.VITE_USE_DIRECT_API !== "true"
    ? ""
    : import.meta.env.VITE_API_URL || "";

export const httpClient = new HttpClient({
  baseURL: apiBaseURL,
  securityWorker: (token) => {
    if (token) {
      return {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      };
    }
  },
});

// Initialize security data from tokenStorage
const token = tokenStorage.getAccessToken();
if (token) {
  httpClient.setSecurityData(token);
  httpClient.instance.defaults.headers.common["Authorization"] = `Bearer ${token}`;
}

// Request interceptor to ensure direct httpClient.instance calls include Bearer token
httpClient.instance.interceptors.request.use((config) => {
  const currentToken =
    httpClient.securityData || tokenStorage.getAccessToken();
  if (currentToken && !config.headers?.Authorization) {
    config.headers = config.headers || {};
    config.headers.Authorization = `Bearer ${currentToken}`;
  }
  return config;
});

let isRefreshing = false;
let failedQueue = [];

const processQueue = (error, token = null) => {
  failedQueue.forEach((prom) => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });
  failedQueue = [];
};

const PUBLIC_PATH_PREFIXES = [
  "/oauth/authorize",
  "/auth/callback",
  "/data-deletion",
  "/public/",
  "/dispatch/",
  "/public/maintenance/",
  "/sign/",
  "/homysign/",
  "/accept-invitation",
  "/magic-link",
  "/share/",
  "/help",
  "/support/help-center",
  "/register",
  "/signup",
  "/forgot-password",
  "/reset-password",
];

function isPublicRoute(pathname) {
  if (!pathname) return false;
  return PUBLIC_PATH_PREFIXES.some(
    (prefix) => pathname.startsWith(prefix) || pathname === prefix.replace(/\/$/, "")
  );
}

function shouldRedirectToLogin() {
  if (typeof window === "undefined") return false;
  const path = window.location.pathname || "";
  if (path === "/login") return false;
  return !isPublicRoute(path);
}

// Add response interceptor for 402 (quota exceeded) and 401 with silent refresh
httpClient.instance.interceptors.response.use(
  (response) => response,
  async (error) => {
    // Intercept HTTP 402 Payment Required or QUOTA_EXCEEDED error code
    if (
      error.response?.status === 402 ||
      error.response?.data?.code === "QUOTA_EXCEEDED"
    ) {
      const quotaPayload = error.response?.data || {};
      openUpgradeModal(quotaPayload);
      return Promise.reject(error);
    }

    const originalRequest = error.config;

    if (error.response?.status === 401 && originalRequest && !originalRequest._retry) {
      const requestUrl = originalRequest.url || "";
      const AUTH_ENTRYPOINTS = [
        "/api/core/auth/refresh",
        "/api/core/auth/local",
        "/api/core/auth/login",
        "/api/core/auth/signup",
        "/api/core/auth/apple",
        "/api/core/auth/passkey",
        "/api/core/auth/social",
        "/api/core/auth/magic-link",
      ];
      if (AUTH_ENTRYPOINTS.some((url) => requestUrl.includes(url))) {
        const isSocialLinkWhileLoggedIn =
          requestUrl.includes("/api/core/auth/social") && Boolean(tokenStorage.getUser());
        if (!isSocialLinkWhileLoggedIn) {
          clearAuthSession();
          if (shouldRedirectToLogin()) {
            window.location.href = "/login";
          }
        }
        return Promise.reject(error);
      }

      const refreshToken = tokenStorage.getRefreshToken();
      if (!refreshToken) {
        clearAuthSession();
        if (shouldRedirectToLogin()) {
          window.location.href = "/login";
        }
        return Promise.reject(error);
      }

      if (isRefreshing) {
        originalRequest._retry = true;
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        })
          .then((token) => {
            originalRequest._retry = true;
            originalRequest.headers = originalRequest.headers || {};
            originalRequest.headers.Authorization = `Bearer ${token}`;
            return httpClient.instance(originalRequest);
          })
          .catch((err) => Promise.reject(err));
      }

      originalRequest._retry = true;
      isRefreshing = true;

      try {
        const response = await httpClient.instance.post(
          "/api/core/auth/refresh",
          { refreshToken },
          { _retry: true }
        );
        const { token: newAccessToken, refreshToken: newRefreshToken } = response.data;

        tokenStorage.setAccessToken(newAccessToken);
        httpClient.setSecurityData(newAccessToken);
        if (httpClient.instance?.defaults?.headers) {
          httpClient.instance.defaults.headers.common = httpClient.instance.defaults.headers.common || {};
          httpClient.instance.defaults.headers.common["Authorization"] = `Bearer ${newAccessToken}`;
        }
        if (newRefreshToken) {
          tokenStorage.setRefreshToken(newRefreshToken);
        }

        saveAuthSession(newAccessToken, null, newRefreshToken);

        processQueue(null, newAccessToken);

        originalRequest.headers = originalRequest.headers || {};
        originalRequest.headers.Authorization = `Bearer ${newAccessToken}`;
        return httpClient.instance(originalRequest);
      } catch (refreshErr) {
        processQueue(refreshErr, null);
        clearAuthSession();
        if (shouldRedirectToLogin()) {
          window.location.href = "/login";
        }
        return Promise.reject(refreshErr);
      } finally {
        isRefreshing = false;
      }
    }

    return Promise.reject(error);
  }
);

export const Admin = {
  api: new AdminClass(httpClient),

};

export const Assets = {
  api: new AssetsClass(httpClient),
};

const applicationsApiInstance = new ApplicationsClass(httpClient);
applicationsApiInstance.listDocuments = async (applicationId, params = {}) => {
  const res = await Assets.api.listAssets(
    { entityType: "application", entityId: applicationId },
    params,
  );
  return res?.assets || [];
};
applicationsApiInstance.deleteDocument = (applicationId, assetId, params = {}) =>
  Assets.api.deleteAsset(assetId, params);

export const Applications = {
  api: applicationsApiInstance,
  ...(Models["Applications"] || {}),
};

export const Calendar = {
  api: new CalendarClass(httpClient),

};

export const Core = {
  api: new CoreClass(httpClient),
  ...(Models["Core"] || {})
};

export const Databases = {
  api: new DatabasesClass(httpClient),
  listDatabases: (params) => Databases.api.listDatabases(params),
};

export const Health = {
  api: new HealthClass(httpClient),

};

export const Inbox = {
  api: new InboxClass(httpClient),

};

export const Listings = {
  api: new ListingsClass(httpClient),
  ...(Models["Listings"] || {})
};

export const Maintenance = {
  api: new MaintenanceClass(httpClient),
  ...(Models["Maintenance"] || {})
};
Maintenance.api.calculateTax = (data, params) => Maintenance.api.calculateTaxPreview(data, params);
Maintenance.api.updateStatementTaxCompliance = (statementId, data, params) => Maintenance.api.calculateStatementTaxCompliance(statementId, data, params);

export const Marketplace = {
  api: new MarketplaceClass(httpClient),
};
export const MarketplaceApi = Marketplace.api;

export const Notes = {
  api: new NotesClass(httpClient),

};

export const Organizations = {
  api: new OrganizationsClass(httpClient),

};

export const Properties = {
  api: new PropertiesClass(httpClient),
  ...(Models["Properties"] || {})
};

export const Public = {
  api: new PublicClass(httpClient),

};
Public.api.getPublicGuarantor = (token, params) => Public.api.getPublicGuarantorIntake(token, params);
Public.api.submitPublicGuarantor = (token, data, params) => Public.api.submitPublicGuarantorSubApplication(token, data, params);

export const Rentals = {
  api: new RentalsClass(httpClient),
  ...(Models["Rentals"] || {})
};
Rentals.api.listGuarantees = (rentalId, params) => Rentals.api.listRentalGuarantees(rentalId, params);
Rentals.api.getAllPayments = (params = {}) => Rentals.api.listPaymentTransactions(params.query || params, params);

export const BuildingExpenses = {
  api: new BuildingExpensesClass(httpClient),
};
export const BuildingExpensesApi = BuildingExpenses.api;

export const LegalNotices = {
  api: new LegalNoticesClass(httpClient),
};
export const LegalNoticesApi = LegalNotices.api;

export const StampTax = {
  api: new StampTaxClass(httpClient),
};
export const StampTaxApi = StampTax.api;

export const TrustDeposits = {
  api: new TrustDepositsClass(httpClient),
};
export const TrustDepositsApi = TrustDeposits.api;

export const NotaryAuthorization = {
  api: new NotaryAuthorizationClass(httpClient),
};
export const NotaryAuthorizationApi = NotaryAuthorization.api;

export const Support = {
  api: new SupportClass(httpClient),

};

export const Tasks = {
  api: new TasksClass(httpClient),

};

export const User = {
  api: new UserClass(httpClient),

};

export const Homysign = {
  api: new HomysignClass(httpClient),

};

export const Homytrust = {
  api: new HomytrustClass(httpClient),
};
export const HomytrustApi = Homytrust.api;

export const Approvals = {
  api: new ApprovalsClass(httpClient),
};
export const ApprovalsApi = Approvals.api;

export const WorkOrders = {
  api: new WorkOrdersClass(httpClient),
};
export const WorkOrdersApi = WorkOrders.api;

export const MaintenanceParts = {
  api: new MaintenancePartsClass(httpClient),
};
export const MaintenancePartsApi = MaintenanceParts.api;

export const GuestSession = {
  api: new GuestSessionClass(httpClient),
  createSession: (data) => GuestSession.api.createSession(data),
  getSession: (token) => GuestSession.api.getSession(token),
  updateDraft: (token, draftType, draftData) => GuestSession.api.updateDraft(token, draftType, draftData),
  claimSession: (token, createApplication) => GuestSession.api.claimSession(token, createApplication),
};
export const GuestSessionApi = GuestSession.api;

export const Workflows = {
  api: new WorkflowsClass(httpClient),
  list: (params) => Workflows.api.list(params),
  get: (id, params) => Workflows.api.get(id, params),
  create: (data, params) => Workflows.api.create(data, params),
  update: (id, data, params) => Workflows.api.update(id, data, params),
  delete: (id, params) => Workflows.api.delete(id, params),
  getCanvas: (id, params) => Workflows.api.getCanvas(id, params),
  saveCanvas: (id, data, params) => Workflows.api.saveCanvas(id, data, params),
  listTemplates: (params) => Workflows.api.listTemplates(params),
  getTemplate: (id, params) => Workflows.api.getTemplate(id, params),
  createFromTemplate: (templateId, name, options, params) =>
    Workflows.api.createFromTemplate(templateId, name, options, params),
};
export const WorkflowsApi = Workflows.api;

export const Payments = { api: new PaymentsClass(httpClient) };
export const PaymentsApi = PaymentsClass;

export const Contracts = {
  api: new ContractsClass(httpClient),
};
export const ContractsApi = Contracts.api;

export const Memberships = {
  api: new MembershipsClass(httpClient),
};
export const MembershipsApi = Memberships.api;

export const Agents = {
  api: new AgentsClass(httpClient),
};
export const AgentsApi = Agents.api;

export const Dossiers = {
  api: new DossiersClass(httpClient),
};
export const DossiersApi = Dossiers.api;

export const Platform = {
  api: new PlatformClass(httpClient),
};
export const PlatformApi = Platform.api;

export const setAuthToken = (token) => {
  httpClient.setSecurityData(token || null);
  if (token) {
    httpClient.instance.defaults.headers.common["Authorization"] = `Bearer ${token}`;
  } else {
    delete httpClient.instance.defaults.headers.common["Authorization"];
  }
};

export const http = httpClient.instance;

export * from "./data-contracts";

